import React, { useState, useMemo } from 'react';
import { useVisualizationStore } from '../store/visualizationStore';
import { VisualizationNode } from '../types/infrastructure';

export const SearchPanel: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('all');

  const {
    nodes,
    zoomToNode
  } = useVisualizationStore();

  // Filter nodes based on search term and type
  const filteredNodes = useMemo(() => {
    let filtered = nodes;

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(node => node.type === selectedType);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(node => 
        node.name.toLowerCase().includes(term) ||
        node.id.toLowerCase().includes(term) ||
        (node.metadata && JSON.stringify(node.metadata).toLowerCase().includes(term))
      );
    }

    return filtered.slice(0, 50); // Limit results for performance
  }, [nodes, searchTerm, selectedType]);

  const handleNodeClick = (node: VisualizationNode) => {
    zoomToNode(node.id);
    setIsOpen(false);
  };

  const nodeTypes = ['all', 'site', 'pod', 'rack', 'device', 'component'];

  const getNodeIcon = (type: string) => {
    const icons = {
      site: '🌍',
      pod: '🏢',
      rack: '📦',
      device: '💾',
      component: '🔧'
    };
    return icons[type as keyof typeof icons] || '📍';
  };

  return (
    <div className="search-panel absolute top-4 right-4 z-20">
      {/* Search Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-white shadow-lg rounded-lg p-3 hover:shadow-xl transition-shadow"
        >
          🔍
        </button>
      )}

      {/* Search Panel */}
      {isOpen && (
        <div className="bg-white shadow-lg rounded-lg p-4 w-80 max-h-96 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold text-gray-800">Search Infrastructure</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ×
            </button>
          </div>

          {/* Search Input */}
          <div className="mb-3">
            <input
              type="text"
              placeholder="Search by name, ID, or metadata..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>

          {/* Type Filter */}
          <div className="mb-3">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {nodeTypes.map(type => (
                <option key={type} value={type}>
                  {type === 'all' ? 'All Types' : `${type.charAt(0).toUpperCase()}${type.slice(1)}s`}
                </option>
              ))}
            </select>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600 mb-2">
            {filteredNodes.length} result{filteredNodes.length !== 1 ? 's' : ''}
            {filteredNodes.length === 50 && ' (showing first 50)'}
          </div>

          {/* Results List */}
          <div className="flex-1 overflow-y-auto">
            {filteredNodes.length === 0 ? (
              <div className="text-center text-gray-500 py-4">
                {searchTerm ? 'No results found' : 'Start typing to search...'}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredNodes.map(node => (
                  <button
                    key={node.id}
                    onClick={() => handleNodeClick(node)}
                    className="w-full text-left px-3 py-2 rounded hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{getNodeIcon(node.type)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {node.name}
                        </div>
                        <div className="text-sm text-gray-500 truncate">
                          {node.type} • {node.id}
                        </div>
                        {node.metadata && (
                          <div className="text-xs text-gray-400 truncate">
                            {typeof node.metadata === 'object' 
                              ? Object.entries(node.metadata).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(', ')
                              : String(node.metadata)
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick Filters */}
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-600 mb-2">Quick Filters:</div>
            <div className="flex flex-wrap gap-1">
              {nodeTypes.slice(1).map(type => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    selectedType === type
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {getNodeIcon(type)} {type}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};